import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FolderPlus, Plus, Trash2, Loader2, Upload, Users, Save, Star, ImageIcon, X, UserCog,
} from "lucide-react";
import { playerAvatar } from "@/components/plantel/PlayerCard";

export default function AdminPlantelTab() {
  const [rosters, setRosters] = useState([]);
  const [limits, setLimits] = useState(null);
  const [categories, setCategories] = useState([]);
  const [roster, setRoster] = useState(null); // selected roster (with players)
  const [player, setPlayer] = useState(null); // selected player being edited
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const mainRef = useRef(null);
  const extraRef = useRef(null);
  const coverRef = useRef(null);

  const loadRosters = useCallback(async () => {
    const [r, lim] = await Promise.all([
      api.get("/plantel/rosters").then((x) => x.data),
      api.get("/plantel/limits").then((x) => x.data).catch(() => null),
    ]);
    setRosters(r);
    if (lim) setLimits(lim);
  }, []);

  useEffect(() => {
    loadRosters();
    api.get("/plantel/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, [loadRosters]);

  const openRoster = async (slug) => {
    setPlayer(null);
    const { data } = await api.get(`/plantel/rosters/by-slug/${slug}`);
    setRoster(data);
  };
  const reloadRoster = async () => { if (roster) await openRoster(roster.slug); };

  const atLimit = limits && limits.rosters_used >= limits.rosters_max;

  const createRoster = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/plantel/rosters", {
        category: fd.get("category"),
        season: fd.get("season"),
        subcategory: fd.get("subcategory") || null,
        description: fd.get("description") || "",
      });
      e.target.reset();
      toast.success("Plantel creado");
      loadRosters();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  const saveRosterMeta = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/plantel/rosters/${roster.id}`, {
        category: fd.get("category"),
        season: fd.get("season"),
        subcategory: fd.get("subcategory") || null,
        description: fd.get("description") || "",
      });
      setRoster((r) => ({ ...r, ...data }));
      toast.success("Plantel actualizado");
      loadRosters();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
  };

  const deleteRoster = async () => {
    if (!window.confirm("¿Eliminar este plantel y todos sus jugadores?")) return;
    await api.delete(`/plantel/rosters/${roster.id}`);
    setRoster(null); setPlayer(null);
    toast.success("Plantel eliminado");
    loadRosters();
  };

  const uploadCover = async (file) => {
    if (!file || !roster) return;
    setUploadingCover(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/plantel/rosters/${roster.id}/cover`, fd);
      setRoster((r) => ({ ...r, cover_url: data.cover_url }));
      loadRosters();
      toast.success("Portada actualizada");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally {
      setUploadingCover(false);
      if (coverRef.current) coverRef.current.value = "";
    }
  };

  const removeCover = async () => {
    try {
      const { data } = await api.delete(`/plantel/rosters/${roster.id}/cover`);
      setRoster((r) => ({ ...r, cover_url: data.cover_url }));
      loadRosters();
      toast.success("Portada quitada — se usará una foto al azar de un jugador");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo quitar"); }
  };

  const createPlayer = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (!fd.get("name")) return;
    try {
      await api.post("/plantel/players", {
        roster_id: roster.id,
        name: fd.get("name"),
        position: fd.get("position") || "",
        number: fd.get("number") || "",
      });
      e.target.reset();
      toast.success("Jugador agregado");
      reloadRoster();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo agregar"); }
  };

  const savePlayer = async (e) => {
    e.preventDefault();
    setSavingP(true);
    const fd = new FormData(e.target);
    const body = {};
    ["name", "position", "number", "birthdate", "height", "nationality", "instagram", "bio", "trajectory", "other"]
      .forEach((f) => { body[f] = fd.get(f) || ""; });
    try {
      const { data } = await api.patch(`/plantel/players/${player.id}`, body);
      setPlayer((p) => ({ ...p, ...data }));
      toast.success("Ficha guardada");
      reloadRoster();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingP(false); }
  };

  const deletePlayer = async (id) => {
    if (!window.confirm("¿Eliminar este jugador?")) return;
    await api.delete(`/plantel/players/${id}`);
    if (player?.id === id) setPlayer(null);
    toast.success("Jugador eliminado");
    reloadRoster();
  };

  const uploadPhoto = async (file, main) => {
    if (!file || !player) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("main", main ? "true" : "false");
    try {
      const { data } = await api.post(`/plantel/players/${player.id}/photo`, fd);
      setPlayer(data);
      reloadRoster();
      toast.success("Foto subida");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally {
      setUploading(false);
      if (mainRef.current) mainRef.current.value = "";
      if (extraRef.current) extraRef.current.value = "";
    }
  };

  const removePhoto = async (url) => {
    const { data } = await api.delete(`/plantel/players/${player.id}/photo`, { params: { url } });
    setPlayer(data);
    reloadRoster();
  };

  const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
      {/* Rosters column */}
      <section className="lg:col-span-1">
        <form onSubmit={createRoster} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-navy font-bold"><FolderPlus className="h-5 w-5 text-gold" /> Nuevo plantel</div>
          {limits && (
            <div data-testid="rosters-limit" className={`mt-3 text-xs font-semibold rounded-lg px-3 py-2 ${atLimit ? "bg-[#c0392b]/10 text-[#c0392b]" : "bg-[#eef1f7] text-navy"}`}>
              {limits.rosters_used} / {limits.rosters_max} planteles · máx {limits.players_max} jugadores
            </div>
          )}
          <select name="category" data-testid="roster-category" className={`mt-4 bg-white ${field}`}>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input name="season" data-testid="roster-season" placeholder="Temporada (ej: 2026)" className={`mt-3 ${field}`} />
          <input name="subcategory" data-testid="roster-subcategory" placeholder="Subcategoría (opcional, ej: Sub-12)" className={`mt-3 ${field}`} />
          <textarea name="description" rows={2} placeholder="Descripción (opcional)" className={`mt-3 resize-none ${field}`} />
          <button data-testid="roster-create-btn" disabled={atLimit} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
            <Plus className="h-4 w-4" /> Crear plantel
          </button>
        </form>

        <div className="mt-5 space-y-2">
          {rosters.map((r) => (
            <button
              key={r.id}
              data-testid={`roster-item-${r.slug}`}
              onClick={() => openRoster(r.slug)}
              className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                roster?.id === r.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold">{r.category}{r.subcategory ? ` · ${r.subcategory}` : ""}</span>
                <span className={`text-xs ${roster?.id === r.id ? "text-gold" : "text-gray-400"}`}>{r.player_count} jug.</span>
              </div>
              <div className={`text-xs ${roster?.id === r.id ? "text-white/60" : "text-gray-400"}`}>Temporada {r.season}</div>
            </button>
          ))}
          {rosters.length === 0 && <p className="text-sm text-gray-400 px-1">Todavía no hay planteles.</p>}
        </div>
      </section>

      {/* Players + editor column */}
      <section className="lg:col-span-2">
        {!roster ? (
          <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
            <Users className="h-10 w-10" />
            <p className="mt-3">Seleccioná un plantel para gestionar sus jugadores</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Roster meta */}
            <form onSubmit={saveRosterMeta} key={roster.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-navy font-bold"><UserCog className="h-5 w-5 text-gold" /> {roster.title}</div>
                <button type="button" onClick={deleteRoster} data-testid="delete-roster-btn" className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#c0392b]/5">
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              </div>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <select name="category" defaultValue={roster.category} className={`bg-white ${field}`}>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input name="season" defaultValue={roster.season} placeholder="Temporada" className={field} />
                <input name="subcategory" defaultValue={roster.subcategory || ""} placeholder="Subcategoría" className={field} />
                <textarea name="description" defaultValue={roster.description || ""} rows={2} placeholder="Descripción" className={`sm:col-span-3 resize-none ${field}`} />
              </div>
              <button data-testid="save-roster-meta" className="mt-3 inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#16235a]">
                <Save className="h-4 w-4" /> Guardar plantel
              </button>

              <div className="mt-5 pt-5 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm font-semibold text-navy"><ImageIcon className="h-4 w-4 text-gold" /> Foto de portada</div>
                <p className="mt-1 text-xs text-gray-400">Si no cargás una, se mostrará una foto al azar de un jugador del plantel.</p>
                <div className="mt-3 flex items-start gap-4">
                  <div className="w-40 shrink-0">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border border-gray-100 bg-navy">
                      <img src={roster.cover_url || roster.sample_photo_url || playerAvatar(roster.title)} alt="portada" className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-1 text-[11px] text-center text-gray-400">
                      {roster.cover_url ? "Portada fija" : "Foto al azar de jugador"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label data-testid="roster-cover-upload" className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:border-navy/40">
                      {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 text-gold" />}
                      {roster.cover_url ? "Cambiar portada" : "Subir portada"}
                      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} disabled={uploadingCover} />
                    </label>
                    {roster.cover_url && (
                      <button type="button" data-testid="roster-cover-remove" onClick={removeCover} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#c0392b] border border-[#c0392b]/30 rounded-lg px-3 py-2 hover:bg-[#c0392b]/5">
                        <X className="h-3.5 w-3.5" /> Quitar portada
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* Add player */}
            <form onSubmit={createPlayer} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 text-navy font-bold"><Plus className="h-5 w-5 text-gold" /> Agregar jugador</div>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <input name="name" data-testid="player-name-input" placeholder="Nombre y apellido" className={`sm:col-span-1 ${field}`} />
                <input name="position" placeholder="Posición" className={field} />
                <input name="number" placeholder="Número" className={field} />
              </div>
              <button data-testid="player-create-btn" className="mt-3 inline-flex items-center gap-2 bg-gold text-navy font-semibold px-4 py-2 rounded-lg hover:opacity-90">
                <Plus className="h-4 w-4" /> Agregar
              </button>
            </form>

            {/* Players list */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <p className="text-sm font-semibold text-navy">Jugadores ({roster.players?.length || 0})</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(roster.players || []).map((p) => (
                  <button
                    key={p.id}
                    data-testid={`player-item-${p.slug}`}
                    onClick={() => setPlayer(p)}
                    className={`group relative rounded-xl overflow-hidden border text-left ${player?.id === p.id ? "border-navy ring-2 ring-navy/30" : "border-gray-100 hover:border-navy/30"}`}
                  >
                    <div className="aspect-[3/4] bg-navy">
                      <img src={p.photo_url || playerAvatar(p.name)} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <span onClick={(e) => { e.stopPropagation(); deletePlayer(p.id); }} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-lg bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#c0392b]">
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                    <div className="p-2">
                      <div className="text-xs font-bold text-navy truncate">{p.name}</div>
                      <div className="text-[11px] text-gray-400 truncate">{p.position || "—"}{p.number ? ` · #${p.number}` : ""}</div>
                    </div>
                  </button>
                ))}
              </div>
              {(!roster.players || roster.players.length === 0) && <p className="mt-3 text-sm text-gray-400">Sin jugadores todavía.</p>}
            </div>

            {/* Player editor */}
            {player && (
              <form onSubmit={savePlayer} key={player.id} data-testid="player-editor" className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                <div className="flex items-center gap-2 text-navy font-bold"><UserCog className="h-5 w-5 text-gold" /> Ficha de {player.name}</div>

                {/* Photos */}
                <div className="mt-4 flex flex-wrap gap-3 items-start">
                  <div className="relative w-28 shrink-0">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden border border-gray-100 bg-navy">
                      <img src={player.photo_url || playerAvatar(player.name)} alt={player.name} className="w-full h-full object-cover" />
                    </div>
                    <label className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg py-1.5 cursor-pointer hover:border-navy/40">
                      <Star className="h-3.5 w-3.5 text-gold" /> Principal
                      <input ref={mainRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], true)} disabled={uploading} />
                    </label>
                    {player.photo_url && (
                      <button type="button" onClick={() => removePhoto(player.photo_url)} className="mt-1.5 w-full text-[11px] text-[#c0392b] hover:underline">Quitar principal</button>
                    )}
                  </div>

                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 text-sm font-semibold text-navy"><ImageIcon className="h-4 w-4 text-gold" /> Fotos adicionales ({(player.photos || []).length}/3)</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(player.photos || []).map((u) => (
                        <div key={u} className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-100">
                          <img src={u} alt="extra" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removePhoto(u)} className="absolute top-1 right-1 h-6 w-6 rounded bg-black/50 text-white flex items-center justify-center hover:bg-[#c0392b]"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                      {(player.photos || []).length < 3 && (
                        <label className="h-20 w-20 rounded-lg border border-dashed border-gray-300 flex items-center justify-center cursor-pointer text-gray-400 hover:border-navy/40">
                          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                          <input ref={extraRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], false)} disabled={uploading} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fields */}
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <input name="name" defaultValue={player.name} placeholder="Nombre" className={field} />
                  <input name="position" defaultValue={player.position || ""} placeholder="Posición" className={field} />
                  <input name="number" defaultValue={player.number || ""} placeholder="Número" className={field} />
                  <input name="birthdate" type="date" defaultValue={player.birthdate || ""} className={`text-gray-600 bg-white ${field}`} />
                  <input name="height" defaultValue={player.height || ""} placeholder="Altura (ej: 1.78 m)" className={field} />
                  <input name="nationality" defaultValue={player.nationality || ""} placeholder="Nacionalidad" className={field} />
                  <input name="instagram" defaultValue={player.instagram || ""} placeholder="Instagram (@usuario)" className={`sm:col-span-2 ${field}`} />
                  <textarea name="bio" defaultValue={player.bio || ""} rows={3} placeholder="Biografía" className={`sm:col-span-2 resize-none ${field}`} />
                  <textarea name="trajectory" defaultValue={player.trajectory || ""} rows={3} placeholder="Trayectoria" className={`sm:col-span-2 resize-none ${field}`} />
                  <textarea name="other" defaultValue={player.other || ""} rows={2} placeholder="Otros datos" className={`sm:col-span-2 resize-none ${field}`} />
                </div>
                <button data-testid="save-player-btn" disabled={savingP} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
                  {savingP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar ficha
                </button>
              </form>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
